import React, { useState, useEffect } from "react";
import {
  useNavigate,
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { EmailNotConfirmedError } from "../errors";
import type { AuthClient } from "../authClient";
import { validateEmail } from "../../utils";
import { AuthPage } from "./AuthPage";

export interface AuthLoginProps {
  appTitle: string;
  logo: string;
  /** Used for `resendConfirmationEmail` when login fails because the email isn't confirmed yet. */
  authClient: AuthClient;
  /** Fallback path after login when no `?redirectTo=` / location.state redirect is present. */
  defaultRedirect?: string;
}

function resolvePostLoginPath(
  searchRedirect: string | null,
  stateRedirect: string | undefined,
  defaultRedirect: string,
): string {
  const candidate = searchRedirect ?? stateRedirect ?? defaultRedirect;
  return candidate.startsWith("/") ? candidate : defaultRedirect;
}

export function Login({
  appTitle,
  logo,
  authClient,
  defaultRedirect = "/dashboard",
}: AuthLoginProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [isEmailNotConfirmed, setIsEmailNotConfirmed] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setSuccess(state.message);
      setError("");
    }
  }, [location]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!validateEmail(email)) {
      errors.email = t("EmailInvalid");
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    const error = value && !validateEmail(value) ? t("EmailInvalid") : "";
    setValidationErrors((prev) => ({ ...prev, email: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsEmailNotConfirmed(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await login(email.trim(), password.trim());
      const state = location.state as { redirectTo?: string } | null;
      navigate(
        resolvePostLoginPath(
          searchParams.get("redirectTo"),
          state?.redirectTo,
          defaultRedirect,
        ),
        { replace: true },
      );
    } catch (err) {
      setSuccess("");
      setIsEmailNotConfirmed(err instanceof EmailNotConfirmedError);
      setError(err instanceof Error ? err.message : "LoginFailed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email.trim() || resendLoading) return;
    setResendLoading(true);
    setError("");
    try {
      await authClient.resendConfirmationEmail(email);
      setSuccess("ResendConfirmationSuccess");
      setIsEmailNotConfirmed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "ResendConfirmationFailed");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <AuthPage appTitle={appTitle} logo={logo}>
      <Typography
        component="h2"
        variant="body1"
        sx={{
          mb: 4,
          color: "text.secondary",
        }}
      >
        {t("Login")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 3, borderRadius: 2 }}>
          <Box>
            <Typography component="span" variant="body2">
              {t(error)}
            </Typography>
            {isEmailNotConfirmed && (
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={handleResendConfirmation}
                disabled={resendLoading}
                sx={{
                  display: "block",
                  mt: 1,
                  p: 0,
                  border: "none",
                  background: "none",
                  cursor: resendLoading ? "default" : "pointer",
                  textDecoration: "underline",
                  font: "inherit",
                  "&:hover": { color: "error.dark" },
                  "&:disabled": { opacity: 0.7 },
                }}
              >
                {resendLoading ? t("Loading...") : t("ResendConfirmationMail")}
              </Typography>
            )}
          </Box>
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ width: "100%", mb: 3, borderRadius: 2 }}
        >
          {t(success)}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t("Email")}
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t("Password")}
          type={showPassword ? "text" : "password"}
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ width: "100%", textAlign: "right", mb: 2 }}>
          <Typography
            component={Link}
            to="/forgotPassword"
            variant="body2"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("ForgotPassword")}
          </Typography>
        </Box>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600, mb: 2 }}
        >
          {loading ? t("Loading...") : t("Login")}
        </Button>
        <Box sx={{ width: "100%", textAlign: "center", mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("DontHaveAnAccount")}
          </Typography>
          <Button
            component={Link}
            to="/register"
            fullWidth
            variant="outlined"
            size="large"
            sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600 }}
          >
            {t("Register")}
          </Button>
        </Box>
      </Box>
    </AuthPage>
  );
}
